import { useState, useCallback, useEffect, useRef } from 'react';
import { useDataProvider } from '@ui-framework/providers/DataProvider';

async function chatPost(client, question, sessionId) {
  const response = await client.post('/assistant/v1/chat/', {
    question,
    response_format: 'markdown',
    ...(sessionId ? { session_id: sessionId } : {}),
  });
  return response.data;
}

async function chatStream(client, { question, sessionId, onDone, signal }) {
  const requestPayload = {
    question: String(question || ''),
    response_format: 'markdown',
    ...(sessionId ? { session_id: String(sessionId) } : {}),
  };
  const response = await client.stream(
    '/assistant/v1/chat/stream',
    requestPayload,
    {
      signal,
    },
  );
  if (!response.ok) {
    let detail = response.statusText || 'Stream request failed';
    try {
      const body = await response.text();
      const parsed = body ? JSON.parse(body) : null;
      if (parsed && (parsed.detail || parsed.message)) {
        detail =
          typeof parsed.detail === 'string'
            ? parsed.detail
            : parsed.message || detail;
      }
    } catch {
      // keep statusText if body parse fails
    }
    const err = new Error(detail);
    err.status = response.status;
    throw err;
  }
  if (!response.body) {
    const err = new Error('Response has no body');
    err.status = response.status;
    throw err;
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = null;
  let currentData = null;

  function processLines(lineList) {
    for (let i = 0; i < lineList.length; i += 1) {
      const line = lineList[i];
      if (line.startsWith('event:')) {
        currentEvent = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        currentData = line.slice(5).trim();
      } else if (line === '' && currentEvent != null && currentData != null) {
        if (currentEvent === 'token') {
          try {
            JSON.parse(currentData);
          } catch {
            // skip malformed token line
          }
        } else if (currentEvent === 'done') {
          try {
            const payload = JSON.parse(currentData);
            if (onDone) onDone(payload);
          } catch {
            // skip malformed done payload
          }
        }
        currentEvent = null;
        currentData = null;
      }
    }
  }

  async function readStream() {
    const { done, value } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    processLines(lines);
    await readStream();
  }
  try {
    await readStream();
  } catch (streamErr) {
    const err = new Error(
      streamErr.message && streamErr.message.includes('input stream')
        ? 'Stream was interrupted. Try again.'
        : streamErr.message || 'Stream read failed',
    );
    err.cause = streamErr;
    throw err;
  }
  if (currentEvent && currentData != null && currentEvent === 'done') {
    try {
      const payload = JSON.parse(currentData);
      if (onDone) onDone(payload);
    } catch {
      // skip malformed trailing done payload
    }
  }
}

let nextId = 1;

function makeMessage(
  role,
  text,
  sources,
  isError,
  suggestions,
  additionalInfo,
  messageId,
  feedback,
) {
  const id = nextId;
  nextId += 1;
  return {
    id,
    role,
    text,
    sources: sources ?? [],
    isError: isError ?? false,
    suggestions: suggestions ?? [],
    additionalInfo: additionalInfo ?? {
      relatedSnippets: [],
      contextNotes: [],
      sourcesUsed: [],
    },
    messageId: messageId ?? null,
    feedback: feedback ?? null,
  };
}

// Handles the new structured format with additional_info and object suggestions,
// as well as the legacy fallback where the backend embeds a JSON blob in the answer text.
function normalizeResponse(data) {
  let { answer = '', suggestions = [], additionalInfo } = data;
  const { sources, sessionId } = data;

  // Legacy safety: strip embedded trailing JSON blob if present
  const jsonMatch = answer.match(/\{[\s\S]*"answer"\s*:/);
  if (jsonMatch) {
    const jsonStart = answer.indexOf(jsonMatch[0]);
    const rawText = answer.slice(0, jsonStart).trim();
    try {
      const embedded = JSON.parse(answer.slice(jsonStart));
      answer = embedded.answer ?? rawText;
      suggestions = embedded.suggestions ?? suggestions;
      additionalInfo = embedded.additionalInfo ?? additionalInfo;
    } catch {
      answer = rawText;
    }
  }

  // Normalize suggestions: accept both {label, question} objects and plain strings
  const normalizedSuggestions = (
    Array.isArray(suggestions) ? suggestions : []
  ).map(s => (typeof s === 'string' ? { label: s, question: s } : s));

  const normalizedAdditionalInfo = {
    relatedSnippets: additionalInfo?.relatedSnippets ?? [],
    contextNotes: additionalInfo?.contextNotes ?? [],
    sourcesUsed: additionalInfo?.sourcesUsed ?? [],
  };

  return {
    answer,
    suggestions: normalizedSuggestions,
    additionalInfo: normalizedAdditionalInfo,
    sources,
    sessionId,
    messageId: data.message_id ?? data.messageId ?? null,
  };
}

const CHATBOT_PROVIDER =
  process.env.NODE_ENV === 'development' ? 'chatbot' : 'default';

export default function useChatbot({ isOpen, companyType = null }) {
  const client = useDataProvider(CHATBOT_PROVIDER);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [groupName, setGroupName] = useState(companyType);
  const [unreadCount, setUnreadCount] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState({});
  const [expandedNotHelpful, setExpandedNotHelpful] = useState({});
  const [submittingFeedback, setSubmittingFeedback] = useState(null);
  const abortControllerRef = useRef(null);
  const hasAutoLoadedLastSessionRef = useRef(false);

  const loadSession = useCallback(
    async id => {
      if (!id) return;
      setLoadingSession(true);
      setMessages([]);
      try {
        const { data } = await client.get(`/assistant/v1/chat/sessions/${id}`);
        const loaded = (data.messages || []).map(m =>
          makeMessage(
            m.role,
            m.content || '',
            m.sources || [],
            false,
            [],
            {
              relatedSnippets: [],
              contextNotes: [],
              sourcesUsed: [],
            },
            m.id ?? null,
            m.feedback ?? null,
          ),
        );
        setSessionId(data.id);
        setMessages(loaded);
      } catch (_) {
        setMessages([]);
      } finally {
        setLoadingSession(false);
      }
    },
    [client],
  );

  const refetchSessions = useCallback(() => {
    setSessionsLoading(true);
    client
      .get('/assistant/v1/chat/sessions')
      .then(({ data }) => setSessions(Array.isArray(data) ? data : []))
      .catch(() => setSessions([]))
      .finally(() => setSessionsLoading(false));
  }, [client]);

  useEffect(() => {
    if (!isOpen) return;
    hasAutoLoadedLastSessionRef.current = false;
    refetchSessions();
  }, [isOpen, refetchSessions]);

  useEffect(() => {
    if (
      !sessionsLoading &&
      sessions.length > 0 &&
      sessionId == null &&
      messages.length === 0 &&
      !hasAutoLoadedLastSessionRef.current
    ) {
      hasAutoLoadedLastSessionRef.current = true;
      const sorted = [...sessions].sort((a, b) => {
        const aAt = a.updated_at ?? a.created_at ?? '';
        const bAt = b.updated_at ?? b.created_at ?? '';
        return new Date(bAt) - new Date(aAt);
      });
      const lastSessionId = sorted[0]?.id ?? sessions[0]?.id;
      if (lastSessionId) {
        loadSession(lastSessionId);
      }
    }
  }, [sessionsLoading, sessions, sessionId, messages.length, loadSession]);

  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen && abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, [isOpen]);

  const send = useCallback(
    async text => {
      if (!text.trim() || isLoading) return;

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setMessages(prev => [...prev, makeMessage('user', text)]);
      setIsLoading(true);

      const applyDone = data => {
        if (data.sessionId) {
          setSessionId(data.sessionId);
          refetchSessions();
        }
        setMessages(prev => [
          ...prev,
          makeMessage(
            'assistant',
            data.answer,
            data.sources,
            false,
            data.suggestions,
            data.additionalInfo,
            data.messageId,
            null,
          ),
        ]);
        if (!isOpen) setUnreadCount(c => c + 1);
      };

      const setError = message => {
        setMessages(prev => [
          ...prev,
          makeMessage('assistant', message, [], true),
        ]);
      };

      try {
        await chatStream(client, {
          question: text,
          sessionId,
          signal: controller.signal,
          onDone: payload => {
            const data = normalizeResponse(payload);
            applyDone(data);
          },
        });
      } catch (err) {
        if (err.name === 'AbortError') return;
        const msg = err.message || '';
        const isNetworkOrStream =
          msg === 'Failed to fetch' ||
          (msg && msg.includes('NetworkError')) ||
          (msg && msg.includes('input stream')) ||
          (msg &&
            (msg.includes('Stream was interrupted') ||
              msg.includes('Stream read failed'))) ||
          err.status === 404;
        if (isNetworkOrStream) {
          try {
            const raw = await chatPost(client, text, sessionId);
            const data = normalizeResponse(raw);
            applyDone(data);
            return;
          } catch (fallbackErr) {
            setError(
              fallbackErr.message
                ? `Chat unavailable`
                : 'Sorry, something went wrong. Please try again.',
            );
            return;
          }
        }
        const rawMsg = err.message || '';
        let message = 'Sorry, something went wrong. Please try again.';
        if (rawMsg && rawMsg !== 'Stream request failed') {
          message = rawMsg.includes('input stream')
            ? 'Request was invalid or interrupted. Please try again.'
            : rawMsg;
        }
        setError(message);
      } finally {
        abortControllerRef.current = null;
        setIsLoading(false);
      }
    },
    [client, isLoading, sessionId, isOpen, refetchSessions],
  );

  const retry = useCallback(() => {
    setMessages(prev => {
      const withoutError = prev[prev.length - 1]?.isError
        ? prev.slice(0, -1)
        : prev;
      const lastUserMsg = [...withoutError]
        .reverse()
        .find(m => m.role === 'user');
      if (!lastUserMsg) return prev;
      setTimeout(() => send(lastUserMsg.text), 0);
      return withoutError;
    });
  }, [send]);

  const changeGroup = useCallback(id => {
    setGroupName(id);
    setSessionId(null);
    setMessages([]);
  }, []);

  const startNewChat = useCallback(() => {
    setSessionId(null);
    setMessages([]);
  }, []);

  const handleFeedback = useCallback(
    async (messageKey, messageId, helpful, reason, messageIndex) => {
      if (!sessionId) return;
      setSubmittingFeedback(messageKey);
      try {
        let serverMessageId = messageId;
        if (!serverMessageId) {
          const { data } = await client.get(
            `/assistant/v1/chat/sessions/${sessionId}`,
          );
          const serverMessages = data.messages || [];
          setMessages(prev =>
            prev.map((m, i) => ({
              ...m,
              messageId: m.messageId ?? serverMessages[i]?.id ?? null,
            })),
          );
          serverMessageId = serverMessages[messageIndex]?.id ?? null;
        }
        if (serverMessageId) {
          await client.patch(
            `/assistant/v1/chat/sessions/${sessionId}/messages/${serverMessageId}/feedback`,
            { helpful, reason: reason ?? null },
          );
          setMessages(prev =>
            prev.map(m =>
              m.id === messageKey
                ? { ...m, feedback: helpful ? 'helpful' : 'not_helpful' }
                : m,
            ),
          );
        }
        setExpandedNotHelpful(prev => ({ ...prev, [messageKey]: false }));
        setFeedbackReason(prev => ({ ...prev, [messageKey]: '' }));
      } finally {
        setSubmittingFeedback(null);
      }
    },
    [client, sessionId],
  );

  return {
    messages,
    isLoading,
    loadingSession,
    send,
    retry,
    groupName,
    setGroupName: changeGroup,
    unreadCount,
    sessions,
    sessionsLoading,
    refetchSessions,
    loadSession,
    startNewChat,
    handleFeedback,
    feedbackReason,
    setFeedbackReason,
    expandedNotHelpful,
    setExpandedNotHelpful,
    submittingFeedback,
  };
}
